import { useEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { Search } from 'lucide-react';
import {
  type NomenclatureDto,
} from '@eombor/contracts';
import { Input } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { SuggestList, useSuggest, type SuggestOption } from '../../../shared/ui/Suggest.js';
import { useNomenclatures } from '../model/catalog.queries.js';
import { unitText } from '../../../shared/i18n/labels.js';

/** Yozish tugagach shuncha kutiladi — har harfda so'rov ketmaydi. */
export const NOMENCLATURE_SEARCH_DEBOUNCE_MS = 250;

/** Ro'yxatda ko'p qatorni ko'rsatishning ma'nosi yo'q: odam baribir yozib toraytiradi. */
const SUGGEST_LIMIT = 20;

export interface NomenclaturePickerProps {
  /** Tanlangan nomenklatura — maydonda uning kodi va nomi turadi. */
  value: NomenclatureDto | null;
  onSelect: (nomenclature: NomenclatureDto | null) => void;
  label: string;
  hint?: string;
  placeholder?: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  hiddenLabel?: boolean;
}

const labelOf = (item: NomenclatureDto): string => `${item.code} — ${item.name}`;

/**
 * Nomenklatura qidirish — BITTA boshqaruv.
 *
 * Ilgari bu yerda qidiruv maydoni va uning YONIDA yopiq `Select` turardi:
 * odam yozganda ekranda hech nima o'zgarmasdi, chunki natija yopiq ro'yxat
 * ichiga tushardi. Natijasi ko'rinmaydigan qidiruv ishlamaydi, shuning uchun
 * maydon o'z natijasini o'zi ko'rsatadi.
 *
 * Klaviatura bilan to'liq ishlaydi (`↑`/`↓`/`Enter`/`Esc`): ekran
 * skaner-birinchi, sichqoncha shart emas.
 */
export const NomenclaturePicker = ({
  value,
  onSelect,
  label,
  hint,
  placeholder,
  inputRef,
  hiddenLabel = false,
}: NomenclaturePickerProps) => {
  /* Ro'yxat SUZADI va shu tugunga osiladi — maydonning o'zi emas,
     butun o'ram: izoh ham maydonning bir qismi va ro'yxat uning ostidan
     boshlanishi kerak. */
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), NOMENCLATURE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  const nomenclatures = useNomenclatures({
    ...(debounced === '' ? {} : { search: debounced }),
    limit: SUGGEST_LIMIT,
  });

  const items = nomenclatures.data?.items ?? [];
  const options: SuggestOption[] = items.map((item) => ({
    id: item.id,
    label: item.code,
    hint: `${item.name} · ${unitText(item.unit)}`,
  }));

  /* Ro'yxat faqat FOKUS va yozish paytida ochiladi. Tanlangandan keyin u
     yopiladi, aks holda maydonda tanlov ko'rinib turgani holda tagida o'sha
     tanlov yana ro'yxat bo'lib osilib qolardi. */
  const suggest = useSuggest(options.length, open);

  const pick = (index: number): void => {
    const item = items[index];
    if (item === undefined) return;
    onSelect(item);
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (suggest.handleKeyDown(event, { onPick: pick, onEscape: () => setOpen(false) })) return;
    // Ro'yxat yopiq bo'lsa ham `Enter` forma yubormasin: bu ekranda `Enter`
    // "keyingi qadam" degani, sahifani qayta yuklash emas.
    if (event.key === 'Enter') event.preventDefault();
  };

  /* Tanlov bo'lsa maydonda uning nomi turadi; odam yozishni boshlagan zahoti
     yozgani ko'rinadi — qidiruv o'z ustidan yozib ketmaydi. */
  const shown = query !== '' || value === null ? query : labelOf(value);

  return (
    <div ref={anchorRef} className="flex min-w-0 flex-col">
      <Input
        ref={inputRef}
        {...(hiddenLabel ? { 'aria-label': label } : { label })}
        role="combobox"
        aria-expanded={open}
        aria-controls={suggest.listId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && options.length > 0 ? suggest.optionId(suggest.active) : undefined
        }
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        value={shown}
        prefix={<Search size={16} aria-hidden />}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          // Maydon bo'shatildi — tanlov ham bekor bo'ladi, aks holda ekranda
          // bo'sh maydon turib, satrlar eski nomenklatura bilan ochilaverardi.
          if (event.target.value === '' && value !== null) onSelect(null);
        }}
        onKeyDown={handleKeyDown}
        hint={hint}
      />

      {open ? (
        <SuggestList
          anchorRef={anchorRef}
          options={options}
          active={suggest.active}
          listId={suggest.listId}
          optionId={suggest.optionId}
          label={label}
          emptyText={t(
            nomenclatures.isPending ? 'stock.suggestLoading' : 'stock.nomenclatureNotFound',
          )}
          onPick={pick}
          onHover={suggest.setActive}
        />
      ) : null}
    </div>
  );
};
